import { reportMonthToDate, reportNumber, runReport, type ReportResult } from './reports';
import type { DateRangeInput, OnlineBookingVolumePeriod } from './onlineBookingVolume';

/**
 * Phone call reporting, all read from the `calls` dataset of the report
 * system (run_report), so the Control Centre, the small reports and the
 * bots count the same calls the same way.
 */

export type CallReportKind = 'phone_calls' | 'missed_calls' | 'answered_calls';

export type CallMonthYtdRow = {
  monthKey: string;
  label: string;
  count: number;
  isCurrentMonth: boolean;
};

export type CallReportSnapshot = {
  count: number;
  previousCount: number;
  label: string;
};

type Window = { since: string; until: string };

const METRIC_FOR_KIND: Record<CallReportKind, string> = {
  phone_calls: 'calls',
  missed_calls: 'missed',
  answered_calls: 'answered',
};

const LABEL_FOR_KIND: Record<CallReportKind, string> = {
  phone_calls: 'Incoming Phone Calls',
  missed_calls: 'Missed Calls',
  answered_calls: 'Answered Calls',
};

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Local calendar date (YYYY-MM-DD) of a Date. */
function localDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return next.toISOString().slice(0, 10);
}

function daysBetween(since: string, until: string): number {
  const a = Date.UTC(Number(since.slice(0, 4)), Number(since.slice(5, 7)) - 1, Number(since.slice(8, 10)));
  const b = Date.UTC(Number(until.slice(0, 4)), Number(until.slice(5, 7)) - 1, Number(until.slice(8, 10)));
  return Math.round((b - a) / 86_400_000) + 1;
}

/** The same number of days, ending the day before the window starts. */
function previousWindow(window: Window): Window {
  const span = daysBetween(window.since, window.until);
  const until = shiftDate(window.since, -1);
  return { since: shiftDate(until, -(span - 1)), until };
}

/**
 * Current and comparison windows for a period. "This month" compares with
 * the same days of last month, like the rest of the Control Centre.
 */
function windowsFor(
  periodOrRange: OnlineBookingVolumePeriod | DateRangeInput,
  timeZone: string,
): { current: Window; previous: Window } {
  if (typeof periodOrRange !== 'string') {
    const current = { since: localDate(periodOrRange.start), until: localDate(periodOrRange.end) };
    return { current, previous: previousWindow(current) };
  }
  if (periodOrRange === 'last_30_days') {
    const until = reportMonthToDate(timeZone, 0).until;
    const current = { since: shiftDate(until, -29), until };
    return { current, previous: previousWindow(current) };
  }
  return { current: reportMonthToDate(timeZone, 0), previous: reportMonthToDate(timeZone, -1) };
}

function rowNumber(row: Record<string, string | number> | undefined, metric: string): number {
  const raw = row?.[metric];
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function rowsByDimension(result: ReportResult, dimension: string): Map<string, Record<string, string | number>> {
  return new Map(result.rows.map((row) => [String(row[dimension]), row]));
}

/** Same phone / missed call counts as KPI small reports. */
export async function fetchCallReportSnapshot(
  businessId: string,
  kind: CallReportKind,
  periodOrRange: OnlineBookingVolumePeriod | DateRangeInput = 'this_month',
  timeZone: string = browserTimeZone(),
): Promise<CallReportSnapshot> {
  const label = LABEL_FOR_KIND[kind];
  const metric = METRIC_FOR_KIND[kind];
  const { current, previous } = windowsFor(periodOrRange, timeZone);
  try {
    const [now, before] = await Promise.all([
      runReport(businessId, `FROM calls SHOW ${metric} SINCE ${current.since} UNTIL ${current.until}`),
      runReport(businessId, `FROM calls SHOW ${metric} SINCE ${previous.since} UNTIL ${previous.until}`),
    ]);
    return { count: reportNumber(now, metric), previousCount: reportNumber(before, metric), label };
  } catch (err) {
    console.error(`[fetchCallReportSnapshot:${kind}]`, err);
    return { count: 0, previousCount: 0, label };
  }
}

/** Year-to-date inbound (or missed) calls by calendar month. */
export async function fetchCallsByMonthYtd(
  businessId: string,
  kind: CallReportKind,
  timeZone: string = browserTimeZone(),
): Promise<{ months: CallMonthYtdRow[]; yearTotal: number; yearLabel: string }> {
  const metric = METRIC_FOR_KIND[kind];
  const today = reportMonthToDate(timeZone, 0).until;
  const year = today.slice(0, 4);
  const result = await runReport(
    businessId,
    `FROM calls SHOW ${metric} GROUP BY month SINCE ${year}-01-01 UNTIL ${today}`,
  );
  const byMonth = rowsByDimension(result, 'month');
  const currentMonth = Number(today.slice(5, 7));

  const months: CallMonthYtdRow[] = [];
  let yearTotal = 0;
  for (let m = 1; m <= currentMonth; m += 1) {
    const monthKey = `${year}-${String(m).padStart(2, '0')}`;
    const count = rowNumber(byMonth.get(monthKey), metric);
    yearTotal += count;
    months.push({
      monthKey,
      label: new Date(Number(year), m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      count,
      isCurrentMonth: m === currentMonth,
    });
  }

  return { months, yearTotal, yearLabel: year };
}

export type CallDayRow = {
  date: string;
  label: string;
  count: number;
  isToday: boolean;
};

/** Day-by-day inbound (or missed) calls for a calendar month. */
export async function fetchCallsByDayForMonth(
  businessId: string,
  kind: CallReportKind,
  monthKey: string,
  timeZone: string = browserTimeZone(),
): Promise<{ days: CallDayRow[]; monthTotal: number }> {
  const metric = METRIC_FOR_KIND[kind];
  const today = reportMonthToDate(timeZone, 0).until;
  const [year, month] = monthKey.split('-').map(Number);
  const since = `${monthKey}-01`;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const monthEnd = `${monthKey}-${String(lastDay).padStart(2, '0')}`;
  const until = monthEnd < today ? monthEnd : today;

  const result = await runReport(
    businessId,
    `FROM calls SHOW ${metric} GROUP BY day SINCE ${since} UNTIL ${until}`,
  );
  const byDay = rowsByDimension(result, 'day');

  const days: CallDayRow[] = [];
  let monthTotal = 0;
  for (let date = since; date <= until; date = shiftDate(date, 1)) {
    const count = rowNumber(byDay.get(date), metric);
    monthTotal += count;
    const [y, m, d] = date.split('-').map(Number);
    days.push({
      date,
      label: new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      count,
      isToday: date === today,
    });
  }

  return { days, monthTotal };
}

export type CallHourBucket = {
  hour: number;
  answered: number;
  missed: number;
  other: number;
};

/** Inbound calls this period, grouped by hour in the business timezone. */
export async function fetchInboundCallsByHour(
  businessId: string,
  timezone: string,
  period: OnlineBookingVolumePeriod = 'this_month',
): Promise<CallHourBucket[]> {
  const { current } = windowsFor(period, timezone || browserTimeZone());
  const result = await runReport(
    businessId,
    `FROM calls SHOW calls, answered, missed GROUP BY hour SINCE ${current.since} UNTIL ${current.until}`,
  );

  const buckets: CallHourBucket[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    answered: 0,
    missed: 0,
    other: 0,
  }));
  for (const row of result.rows) {
    const hour = Number(String(row.hour).slice(0, 2));
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;
    const answered = rowNumber(row, 'answered');
    const missed = rowNumber(row, 'missed');
    buckets[hour].answered += answered;
    buckets[hour].missed += missed;
    buckets[hour].other += Math.max(0, rowNumber(row, 'calls') - answered - missed);
  }
  return buckets;
}

export type CallBookingConversion = {
  bookings: number;
  previousBookings: number;
};

/**
 * Jobs created in the period after an inbound call from the same customer
 * (the `booked` metric of the calls dataset).
 */
export async function fetchCallBookingConversion(
  businessId: string,
  period: OnlineBookingVolumePeriod = 'this_month',
  timeZone: string = browserTimeZone(),
): Promise<CallBookingConversion> {
  const { current, previous } = windowsFor(period, timeZone);
  const [now, before] = await Promise.all([
    runReport(businessId, `FROM calls SHOW booked SINCE ${current.since} UNTIL ${current.until}`),
    runReport(businessId, `FROM calls SHOW booked SINCE ${previous.since} UNTIL ${previous.until}`),
  ]);
  return { bookings: reportNumber(now, 'booked'), previousBookings: reportNumber(before, 'booked') };
}
